import React, { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@ndd/ui";
import { THEMED_PAGE_TITLE } from "../../lib/always-look";
import {
  ALL_RDS_SCOPE,
  UNASSIGNED_RDS_SCOPE,
  matchesRosterSearch,
  rosterTitle,
  serializeRosterScope,
  sharedSupervisorNames,
  type RosterScope,
} from "../../lib/roster-scope-state";

export interface RosterSupervisorOption {
  id: string;
  name: string;
  email: string;
}

function checkClass(selected: boolean): string {
  return cn("h-4 w-4", selected ? "opacity-100" : "opacity-0");
}

/**
 * Same-named accounts can only be told apart by email, so those rows carry it
 * as a second line; every other row keeps the one-line label it has today.
 */
function SupervisorLabel({
  supervisor,
  showsEmail,
}: {
  supervisor: RosterSupervisorOption;
  showsEmail: boolean;
}) {
  if (!showsEmail) return <span className="flex-1 truncate">{supervisor.name}</span>;

  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate">{supervisor.name}</span>
      <span className="truncate text-xs text-muted-foreground">{supervisor.email}</span>
    </span>
  );
}

/**
 * The /rds page title as the roster picker (NO-506): the h1 IS the trigger,
 * modelled on the sidebar's RdSwitcher (Popover + Command, in-memory
 * filtering). The button sits inside the h1 rather than around it so the
 * heading stays valid HTML and keeps its level for assistive tech.
 */
export function RosterScopeSelector({
  scope,
  supervisors,
  onChange,
}: {
  scope: RosterScope;
  supervisors: RosterSupervisorOption[];
  onChange: (scope: RosterScope) => void;
}) {
  const [open, setOpen] = useState(false);
  const currentSupervisor =
    scope.kind === "supervisor"
      ? supervisors.find((supervisor) => supervisor.id === scope.supervisorId)
      : undefined;
  const title = rosterTitle(scope, currentSupervisor?.name);

  const select = (next: RosterScope): void => {
    setOpen(false);
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <h1
        className={cn(
          "text-3xl font-bold font-sans text-foreground",
          THEMED_PAGE_TITLE
        )}
      >
        <PopoverTrigger asChild>
          {/* Radix's PopoverTrigger injects aria-expanded/aria-controls at
              runtime; declaring role="combobox" statically trips a11y lint
              without them. */}
          <button
            type="button"
            aria-label={`${title} — choose a supervisor`}
            className="inline-flex items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <span>{title}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-subtle" />
          </button>
        </PopoverTrigger>
      </h1>
      <PopoverContent
        align="start"
        className="min-w-[280px] w-[var(--radix-popover-trigger-width)] p-0"
      >
        <RosterScopeList
          scope={scope}
          supervisors={supervisors}
          currentSupervisorId={currentSupervisor?.id}
          onSelect={select}
        />
      </PopoverContent>
    </Popover>
  );
}

/** The picker's rows: All supervisors, each supervisor, then Unassigned. */
export function RosterScopeList({
  scope,
  supervisors,
  currentSupervisorId,
  onSelect: select,
}: {
  scope: RosterScope;
  supervisors: RosterSupervisorOption[];
  currentSupervisorId: string | undefined;
  onSelect: (scope: RosterScope) => void;
}) {
  return (
    <Command
      filter={(_value, search, keywords) => matchesRosterSearch(search, keywords)}
    >
      <CommandInput placeholder="Find a supervisor…" />
      <CommandList>
        <CommandEmpty>No supervisor found.</CommandEmpty>
        {/* cmdk keys highlight, selection and keyboard navigation by `value`,
            so every row needs its own: the scope grammar's reserved words
            for the fixed rows and the user id for supervisors, who can
            share a display name. The filter above reads only `keywords`,
            so typing matches the name and never the id or email. */}
        <CommandGroup>
          <CommandItem
            value={serializeRosterScope(ALL_RDS_SCOPE)}
            keywords={["All supervisors"]}
            onSelect={() => select(ALL_RDS_SCOPE)}
          >
            <Check className={checkClass(scope.kind === "all")} />
            <span className="flex-1 truncate">All supervisors</span>
          </CommandItem>
        </CommandGroup>
        <CommandGroup heading="Supervisors">
          <SupervisorRows
            supervisors={supervisors}
            currentSupervisorId={currentSupervisorId}
            onSelect={select}
          />
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup>
          <CommandItem
            value={serializeRosterScope(UNASSIGNED_RDS_SCOPE)}
            keywords={["Unassigned"]}
            onSelect={() => select(UNASSIGNED_RDS_SCOPE)}
          >
            <Check className={checkClass(scope.kind === "unassigned")} />
            <span className="flex-1 truncate">Unassigned</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/** One row per supervisor, keyed and valued by id; a shared name shows its email. */
function SupervisorRows({
  supervisors,
  currentSupervisorId,
  onSelect: select,
}: {
  supervisors: RosterSupervisorOption[];
  currentSupervisorId: string | undefined;
  onSelect: (scope: RosterScope) => void;
}) {
  const sharedNames = sharedSupervisorNames(supervisors);

  return supervisors.map((supervisor) => {
    const isSharedName = sharedNames.has(supervisor.name.trim());

    return (
      <CommandItem
        key={supervisor.id}
        value={supervisor.id}
        keywords={[supervisor.name]}
        onSelect={() => select({ kind: "supervisor", supervisorId: supervisor.id })}
      >
        <Check className={checkClass(supervisor.id === currentSupervisorId)} />
        <SupervisorLabel supervisor={supervisor} showsEmail={isSharedName} />
      </CommandItem>
    );
  });
}
